import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, FileEdit, Image, ListOrdered, FileWarning, Newspaper, CalendarDays, BarChart3, Bell,
  Mail, MessageCircle, Camera, PlayCircle, BookOpen, CalendarClock, HandHeart, Inbox, FileBarChart,
  Share2, Settings, Menu, LogOut, KeyRound, Loader2, ExternalLink, Rocket, ImageOff, ShieldCheck,
  Phone, Receipt, Megaphone, Timer, PackageCheck, Vote, TrendingDown, PiggyBank, Send, Network, Palette, MapPin, DoorOpen, BellRing, MessageSquareQuote, Signpost, Mountain, HeartPulse
} from 'lucide-react';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getUser, logout, api, setAuth } from '../../lib/api';
import { toast } from 'sonner';

const NAV = [
  { label: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { label: 'Needs Attention', path: '/admin/needs-attention', icon: BellRing },
  { label: 'Launch Readiness', path: '/admin/launch', icon: Rocket },
  { label: 'Launch Content Setup', path: '/admin/launch-content', icon: PackageCheck },
  { label: 'Design Manager', path: '/admin/designs', icon: Palette },
  { label: 'Poster Drop List', path: '/admin/poster-drops', icon: MapPin },
  { label: 'Canvass Log', path: '/admin/canvass', icon: DoorOpen },
  { label: 'Post Scheduler', path: '/admin/scheduler', icon: Send },
  { label: 'PITA Network', path: '/admin/network', icon: Network },
  { label: 'PITA Network Requests', path: '/admin/pita-requests', icon: Inbox },
  { label: 'PITA Supporters', path: '/admin/pita-supporters', icon: HandHeart },
  { label: 'PITA Settings', path: '/admin/pita-settings', icon: Settings },
  { label: 'Media Review', path: '/admin/media-review', icon: ImageOff },
  { label: 'Privacy Audit', path: '/admin/privacy', icon: ShieldCheck },
  { label: 'Pages', path: '/admin/pages', icon: FileText },
  { label: 'Page Content', path: '/admin/page-content', icon: FileEdit },
  { label: 'Hero Manager', path: '/admin/hero', icon: Image },
  { label: 'Paper Ballot Posters', path: '/admin/posters', icon: Vote },
  { label: 'Reviews', path: '/admin/reviews', icon: MessageSquareQuote },
  { label: 'Menu Manager', path: '/admin/menu', icon: ListOrdered },
  { label: 'Issues', path: '/admin/issues', icon: FileWarning },
  { label: 'Email Settings', path: '/admin/email-settings', icon: Mail },
  { label: 'Who Do I Call', path: '/admin/directory', icon: Phone },
  { label: 'Newsroom', path: '/admin/newsroom', icon: Newspaper },
  { label: 'Image Health Check', path: '/admin/image-health', icon: HeartPulse },
  { label: 'Council Meetings', path: '/admin/meetings', icon: CalendarDays },
  { label: 'City Data', path: '/admin/city-data', icon: BarChart3 },
  { label: 'Tax & Infrastructure', path: '/admin/trackers', icon: Receipt },
  { label: 'Campaign Promise & Feed', path: '/admin/campaign', icon: TrendingDown },
  { label: 'Savings Tracker', path: '/admin/savings', icon: PiggyBank },
  { label: 'Have Your Say', path: '/admin/have-your-say', icon: Megaphone },
  { label: 'Alerts', path: '/admin/alerts', icon: Bell },
  { label: '2026 Election Centre', path: '/admin/election', icon: Vote },
  { label: 'Election Events & Refresh', path: '/admin/election-events', icon: CalendarDays },
  { label: 'Newsletter', path: '/admin/newsletter', icon: Mail },
  { label: 'AI Helper / Chat', path: '/admin/chat', icon: MessageCircle },
  { label: 'Gallery', path: '/admin/gallery', icon: Camera },
  { label: 'Beautiful Ward 8 Photos', path: '/admin/ward8-photos', icon: Mountain },
  { label: 'Video Library', path: '/admin/videos', icon: PlayCircle },
  { label: 'Stories', path: '/admin/stories', icon: BookOpen },
  { label: 'Story Media', path: '/admin/story-media', icon: Camera },
  { label: 'Calendar', path: '/admin/calendar', icon: CalendarClock },
  { label: 'Volunteers', path: '/admin/volunteers', icon: HandHeart },
  { label: 'Contact Messages', path: '/admin/messages', icon: Inbox },
  { label: 'Sign Requests', path: '/admin/sign-requests', icon: Signpost },
  { label: 'Reports', path: '/admin/reports', icon: FileBarChart },
  { label: 'Social Sharing', path: '/admin/social', icon: Share2 },
  { label: 'Automation & Audit', path: '/admin/automation', icon: Timer },
  { label: 'Settings', path: '/admin/settings', icon: Settings },
];

const SideNav = ({ location, attentionCount }) => (
  <nav className="p-3 space-y-0.5">
    {NAV.map(({ label, path, icon: Icon }) => {
      const active = path === '/admin' ? location.pathname === '/admin' : (location.pathname === path || location.pathname.startsWith(path + '/'));
      const isAttention = path === '/admin/needs-attention';
      return (
        <Link key={path} to={path} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 ${active ? 'bg-[#EAF2FF] text-[#1a2744]' : 'text-slate-600 hover:bg-slate-100'}`}>
          <Icon className="h-4 w-4 shrink-0" /> {label}
          {isAttention && attentionCount > 0 && (
            <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C9A84C] text-[#1a2744] text-[11px] font-black" data-testid="attention-nav-badge">
              {attentionCount > 99 ? '99+' : attentionCount}
            </span>
          )}
        </Link>
      );
    })}
  </nav>
);

const ChangePasswordDialog = ({ open, onClose, forced }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      const user = getUser();
      if (user) setAuth(localStorage.getItem('w8_token'), { ...user, must_change_password: false });
      toast.success('Password changed successfully');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !forced) onClose(); }}>
      <DialogContent
        data-testid="change-password-dialog"
        className={forced ? '[&>button.absolute]:hidden' : ''}
        onInteractOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
      >
        <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{forced ? 'Password change required' : 'Change password'}</DialogTitle></DialogHeader>
        {forced && <p className="text-sm text-red-900 bg-red-50 border border-red-200 rounded-lg p-3 font-semibold">For security, you must set a new password before using the admin. The default password has been shared and is no longer safe.</p>}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Current password</label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="h-11" data-testid="change-password-current" />
          </div>
          <div>
            <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">New password (min 8 characters)</label>
            <Input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="h-11" data-testid="change-password-new" />
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={busy} className="flex-1 h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="change-password-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Change Password
            </Button>
            {!forced && <Button type="button" variant="outline" onClick={onClose} className="h-11 font-bold btn-press" data-testid="change-password-skip">Cancel</Button>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForced, setPwForced] = useState(false);
  const [attentionCount, setAttentionCount] = useState(0);
  const user = getUser();
  const token = localStorage.getItem('w8_token');

  useEffect(() => { setDrawer(false); }, [location.pathname]);

  // Needs Attention badge: refresh on route change and when items are reviewed
  useEffect(() => {
    if (!token) return undefined;
    const refresh = () => {
      api.get('/admin/attention/summary')
        .then((r) => setAttentionCount(r.data?.show_login_alert ? (r.data?.total || 0) : 0))
        .catch(() => {});
    };
    refresh();
    window.addEventListener('attention-changed', refresh);
    return () => window.removeEventListener('attention-changed', refresh);
  }, [location.pathname, token]);

  useEffect(() => {
    if (user?.must_change_password) { setPwForced(true); setPwOpen(true); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F3F5F8]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#1a2744] text-white h-14 flex items-center px-4 gap-3 shadow">
        <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-white/10" onClick={() => setDrawer(true)} aria-label="Open admin menu" data-testid="admin-menu-button"><Menu className="h-5 w-5" /></button>
        <Link to="/admin" className="font-serif font-black text-base">Ward 8 Admin</Link>
        <span className="hidden sm:inline text-xs text-slate-400">· {user?.email}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <a href="/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/10" title="View site" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" /></a>
          <button onClick={() => { setPwForced(false); setPwOpen(true); }} className="p-2 rounded-lg hover:bg-white/10" title="Change password" data-testid="admin-change-password-button"><KeyRound className="h-4 w-4" /></button>
          <button onClick={() => { logout(); navigate('/admin/login'); }} className="p-2 rounded-lg hover:bg-white/10" title="Log out" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-60 shrink-0 bg-white border-r border-slate-200 min-h-[calc(100vh-56px)] sticky top-14 self-start max-h-[calc(100vh-56px)] overflow-y-auto">
          <SideNav location={location} attentionCount={attentionCount} />
        </aside>

        {/* Mobile drawer */}
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetContent side="left" className="w-[280px] p-0 overflow-y-auto">
            <div className="p-4 border-b"><p className="font-serif font-black text-[#1a2744]">Ward 8 Admin</p></div>
            <SideNav location={location} attentionCount={attentionCount} />
          </SheetContent>
        </Sheet>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} forced={pwForced} />
    </div>
  );
}
